import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, Lock, Eye, EyeOff, Loader2, Sparkles, ChevronRight, Hash } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

export function SignupModal({ open, onOpenChange, referrer, onSwitchToLogin }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Auto-fill referral code from URL ?ref=
  useEffect(() => {
    if (referrer) setReferralCode(referrer);
  }, [referrer, open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!referralCode.trim()) {
      toast.error("Referral code is required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/signup", {
        name: name.trim(),
        email: email.trim(),
        password,
        confirm_password: confirmPassword,
        referral_code: referralCode.trim(),
      });
      localStorage.setItem("umakant_token", r.data.token);
      localStorage.setItem("umakant_user", JSON.stringify(r.data.user));
      toast.success(`Welcome ${r.data.user.name}! Account created.`);
      onOpenChange(false);
      // Hard reload to ensure AuthContext picks up the new token
      window.location.href = "/dashboard";
    } catch (err) {
      const msg = err?.response?.data?.detail || "Signup failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-modal"
        className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-[460px] max-h-[92vh] overflow-y-auto p-0"
      >
        <DialogHeader className="px-5 pt-5">
          <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--purple-300)]">
            <Sparkles size={11} />
            <span>Create Your Account</span>
          </div>
          <DialogTitle className="text-center text-lg font-bold mt-1">
            Sign Up · SMART HUB
          </DialogTitle>
          <DialogDescription className="text-[var(--text-3)] text-[11px] text-center">
            Join the BSC staking community in less than a minute
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="px-5 pb-5 pt-3 space-y-3">
          {/* Full Name */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Full Name
            </label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="signup-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                disabled={busy}
                className="pl-9 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Email (Gmail)
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="signup-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                disabled={busy}
                className="pl-9 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="signup-password"
                type={showPass ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                disabled={busy}
                className="pl-9 pr-10 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
              <button
                type="button"
                onClick={() => setShowPass((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/5 text-[var(--text-3)]"
                tabIndex={-1}
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Confirm Password
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="signup-confirm-password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                disabled={busy}
                className="pl-9 pr-10 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/5 text-[var(--text-3)]"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div className="text-[10px] text-[#F87171] mt-1">Passwords do not match</div>
            )}
          </div>

          {/* Referral Code */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Referral Code <span className="text-[#F87171]">*</span>
            </label>
            <div className="relative">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="signup-referral"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="Sponsor's referral code"
                disabled={busy}
                className="pl-9 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)] mono text-[12px]"
              />
            </div>
            {referrer && (
              <div className="text-[10px] text-[#22C55E] mt-1 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-[#22C55E] shadow-[0_0_4px_#22C55E]" />
                Referral link active — auto-filled
              </div>
            )}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            data-testid="signup-submit"
            disabled={busy}
            className="w-full h-12 font-semibold text-white mt-2 disabled:opacity-50"
            style={{
              background: "var(--gradient-primary)",
              boxShadow: "0 10px 24px -8px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
            }}
          >
            {busy ? (
              <><Loader2 size={15} className="animate-spin mr-2" /> Creating account…</>
            ) : (
              <><Sparkles size={15} className="mr-2" /> Sign Up</>
            )}
          </Button>

          {/* Switch to login */}
          <div className="text-center text-[11px] text-[var(--text-3)] pt-2 border-t border-[var(--card3d-border)]">
            Already have an account?{" "}
            <button
              type="button"
              onClick={onSwitchToLogin}
              data-testid="signup-switch-login"
              className="text-[var(--purple-300)] font-semibold hover:underline inline-flex items-center gap-0.5"
            >
              Login <ChevronRight size={11} />
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LoginModal({ open, onOpenChange, onSwitchToSignup }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!email.includes("@") || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim(), password });
      localStorage.setItem("umakant_token", r.data.token);
      localStorage.setItem("umakant_user", JSON.stringify(r.data.user));
      toast.success(`Welcome back, ${r.data.user.name || "User"}!`);
      onOpenChange(false);
      window.location.href = "/dashboard";
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="login-modal"
        className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-[420px] p-0"
      >
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="text-center text-lg font-bold">
            Login · SMART HUB
          </DialogTitle>
          <DialogDescription className="text-[var(--text-3)] text-[11px] text-center">
            Welcome back — sign in to continue
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="px-5 pb-5 pt-3 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                disabled={busy}
                className="pl-9 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)] block mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                data-testid="login-password"
                type={showPass ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={busy}
                className="pl-9 pr-10 h-11 bg-[var(--surface-1)] border-[var(--card3d-border)]"
              />
              <button
                type="button"
                onClick={() => setShowPass((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/5 text-[var(--text-3)]"
                tabIndex={-1}
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="w-full h-12 font-semibold text-white mt-2 disabled:opacity-50"
            style={{
              background: "var(--gradient-primary)",
              boxShadow: "0 10px 24px -8px rgba(124,58,237,0.55)",
            }}
          >
            {busy ? (
              <><Loader2 size={15} className="animate-spin mr-2" /> Signing in…</>
            ) : (
              <>Login</>
            )}
          </Button>

          <div className="text-center text-[11px] text-[var(--text-3)] pt-2 border-t border-[var(--card3d-border)]">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={onSwitchToSignup}
              data-testid="login-switch-signup"
              className="text-[var(--purple-300)] font-semibold hover:underline inline-flex items-center gap-0.5"
            >
              Sign Up <ChevronRight size={11} />
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
